import React, { FC, useEffect, useMemo, useState, useRef } from "react";
import { Input, Select, Modal, Button, Progress, notification, message, Popconfirm } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import "./SeriesListIn.scss";
import { MiniPaginator, CopyrightFooter, AlertMessage, EpisodeDrawer } from "../../components/common";
import type { AlertConfig } from "../../components/common";
import { createSpeakerApi, deleteSpeakerApi, demoteSpeakerSubtitleApi } from "../../service/speakers";
import { getSeriesListApi, getSeriesLanguagesPageApi, getSeriesDetailApi, deleteSeriesLanguageApi } from "../../service/series";
import { getTaskListApi } from "../../service/folders";
import { getVideoPlayUrl } from "../../utils/videoUrl";
import JSZip from "jszip";
import { globalStore } from "../../utils/globalStore";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { getLanguageName } from "../../utils/languageUtils";
import { UserRoleManager } from "../../utils/userRole";
import { InitService } from "../../utils/initService";

interface VoiceItem {
  id: number;
  name: string;
  seriesId: number;
  characterCount: number;
  isNew?: boolean;
  langCode?: string;
  tagStatus?: number;
  createTime?: string | null;
}

export const SeriesListIn: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // 用于输入框显示，立即更新
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // 防抖定时器
  const [sortBy, setSortBy] = useState('latest');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(8);
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  // 当前剧集信息（从路由参数获取）
  const [seriesId, setSeriesId] = useState<number | null>(null);

  // 弹窗相关状态
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState<string>('');
  const [selectedLangCode, setSelectedLangCode] = useState<string>(''); // 当前选中的语言代码
  const [seriesSpeakers, setSeriesSpeakers] = useState<Speakers.Speaker[]>([]);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<number | null>(null);
  const [selectedSpeakerName, setSelectedSpeakerName] = useState<string>('');
  const [speakerSubtitles, setSpeakerSubtitles] = useState<any[]>([]);
  const [loadingSpeakers, setLoadingSpeakers] = useState(false);
  const [loadingSubtitles, setLoadingSubtitles] = useState(false);
  const [playingClipId, setPlayingClipId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  const CDN_PREFIX = VIDEO_CDN_PREFIX;

  // 新建角色相关状态
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  // 抽屉相关状态 - 使用第一个剧集的信息作为默认值
  const [currentEpisodeInfo, setCurrentEpisodeInfo] = useState({
    name: '',
    sourceLanguage: 'zh',
    targetLanguage: 'en',
    needRemovalSubtitle: false
  });
  const [drawerSpeakers, setDrawerSpeakers] = useState<Speakers.Speaker[]>([]);
  const [drawerSpeakersLoading, setDrawerSpeakersLoading] = useState(false);
  const [characterProfiles, setCharacterProfiles] = useState<Series.CharacterProfile[]>([]);

  const [apiData, setApiData] = useState<{
    data: any[];
    pagination: {
      page: number;
      per_page: number;
      total: number;
      pages: number;
      has_next: boolean;
      has_prev: boolean;
      sort: string;
    };
  }>({
    data: [],
    pagination: {
      page: 1,
      per_page: pageSize,
      total: 0,
      pages: 1,
      has_next: false,
      has_prev: false,
      sort: 'latest'
    }
  });

  // 本地保存权限和菜单，复用 Home 页的权限判断方式
  const [userPermissions, setUserPermissions] = useState<string[]>(
    () => (globalStore.getUserInfo() as any)?.permissions || []
  );
  const [menuListState, setMenuListState] = useState<any[]>(
    () => globalStore.getMenuList() || []
  );

  useEffect(() => {
    const unsubscribe = globalStore.subscribe(() => {
      const user = globalStore.getUserInfo() as any;
      setUserPermissions(user?.permissions || []);
      setMenuListState(globalStore.getMenuList() || []);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const checkSeriesButtonPermission = (permission: string): boolean => {
    if (
      userPermissions &&
      userPermissions.length > 0 &&
      userPermissions.includes(permission)
    ) {
      return true;
    }

    const menuList = menuListState;
    if (!menuList || menuList.length === 0) {
      return false;
    }

    const findMenuPermission = (menus: any[], targetPermission: string): boolean => {
      for (const menu of menus) {
        if (menu.meta?.perms === targetPermission) {
          return true;
        }
        if (menu.children && menu.children.length > 0) {
          if (findMenuPermission(menu.children, targetPermission)) {
            return true;
          }
        }
      }
      return false;
    };

    return findMenuPermission(menuList, permission);
  };

  // 剧集下载权限（与 Home 页保持一致，使用后端权限码 series:download）
  const hasSeriesDownloadPermission = (): boolean => {
    return checkSeriesButtonPermission("series:download");
  };

  // 从 URL 查询参数中解析当前剧集 ID 和名称
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idStr = params.get("seriesId");
    const name = params.get("seriesName") || "";

    const parsedId = idStr ? Number(idStr) : NaN;
    if (!Number.isNaN(parsedId)) {
      setSeriesId(parsedId);
    } else {
      setSeriesId(null);
    }

    if (name) {
      const decodedName = decodeURIComponent(name);
      setCurrentEpisodeInfo((prev) => ({
        ...prev,
        name: decodedName,
      }));
    }
  }, [location.search]);

  // 根据 seriesId 单独获取剧集详情（含 needRemovalSubtitle 等）
  useEffect(() => {
    const fetchSeriesDetail = async () => {
      if (!seriesId) return;

      try {
        const res = await getSeriesDetailApi(seriesId);

        const detail = res.data;
        if (!detail) return;

        setCurrentEpisodeInfo((prev) => ({
          ...prev,
          name: detail.name || prev.name,
          sourceLanguage: detail.sourceLangCode || prev.sourceLanguage,
          // 内层页暂不关心目标语言，这里沿用原值
          needRemovalSubtitle: !!detail.needRemovalSubtitle,
        }));

        // 设置角色信息
        if (detail.characterProfiles && detail.characterProfiles.length > 0) {
          setCharacterProfiles(detail.characterProfiles);
        } else {
          setCharacterProfiles([]);
        }
  
      } catch (e) {
        console.error("获取剧集详情失败:", e);
      }
    };

    fetchSeriesDetail();
  }, [seriesId]);

  // 拉取当前剧集的多语种列表
  useEffect(() => {
    if (!seriesId) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 排序参数映射：1.最新创建，2.最早创建，3.名称排序
        const sortMap: Record<string, number> = {
          'latest': 1,
          'earliest': 2,
          'name-asc': 3,
        };
        
        // 构建请求参数
        const params: Series.GetSeriesLanguagesPageParams = {
          pageNo: page,
          pageSize,
          seriesId,
          sort: sortMap[sortBy] || 1, // 排序参数
        };
        
        // 语种名称查询（绑定到搜索框）
        if (searchQuery.trim()) {
          params.langName = searchQuery.trim();
        }
        
        const res = await getSeriesLanguagesPageApi(params);

        // 适配新接口：{ code, data: { list, total }, msg }
        const list = res?.data?.list || [];
        const total = res?.data?.total ?? 0;

        setApiData((prev) => ({
          data: list,
          pagination: {
            ...prev.pagination,
            page,
            per_page: pageSize,
            total,
            pages: Math.max(1, Math.ceil(total / pageSize || 1)),
            has_next: page * pageSize < total,
            has_prev: page > 1,
          },
        }));
      } catch (err) {
        setAlertConfig({ type: 'error', message: '获取音色库数据失败' });
        setApiData(prev => ({
          data: [],
          pagination: { ...prev.pagination, page: 1, pages: 1, total: 0, has_next: false, has_prev: false }
        }));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, pageSize, seriesId, searchQuery, sortBy]);

  // 获取当前剧集的信息用于抽屉显示（主要是说话人列表）
  // 注意：此页面不调用 getSeriesSpeakersApi 接口，直接设置空数组
  useEffect(() => {
    if (!seriesId) return;
    // 不调用接口，直接设置为空数组
    setDrawerSpeakers([]);
    setDrawerSpeakersLoading(false);
  }, [seriesId]);

  // 过滤和排序数据
  // 将接口数据映射为卡片展示数据
  const apiVoiceItems: VoiceItem[] = useMemo(() => {
    return (apiData.data || []).map((item: any) => {
      const mappedItem = {
        id: item.id ?? item.series_id,
        // 名称优先显示剧集名称，其次回退到其他字段
        name: item.seriesName ?? item.name ?? item.series_name ?? "",
        seriesId: item.seriesId ?? item.series_id ?? item.id,
        characterCount: item.languageCount ?? item.total ?? 0,
        langCode: item.langCode,
        // 确保 tagStatus 和 createTime 正确映射（支持多种可能的字段名）
        tagStatus: item.tagStatus ?? item.tag_status ?? item.tag ?? undefined,
        createTime: item.createTime ?? item.create_time ?? item.createTime ?? null,
      };
      
      return mappedItem;
    });
  }, [apiData]);

  // 后端已处理过滤和排序，直接使用接口返回的数据
  const filteredData = apiVoiceItems;
  const sortedData = filteredData; // 后端已排序，无需前端再次排序

  // 直接使用后端分页信息
  const pages = apiData.pagination.pages;
  const hasPrev = apiData.pagination.has_prev;
  const hasNext = apiData.pagination.has_next;
  const pagedData = sortedData; // 当前页数据

  // 语言维度的批量下载：使用与 Home 页相同的逻辑，但按语种过滤
  const handleLanguageDownload = async (item: VoiceItem) => {
    if (!item.seriesId) {
      message.error("缺少剧集信息，无法下载");
      return;
    }

    if (!item.langCode) {
      message.error("缺少语种信息，无法下载");
      return;
    }

    if (!hasSeriesDownloadPermission()) {
      message.error("暂无剧集下载权限");
      return;
    }

    const notificationKey = `download-progress-${Date.now()}`;

    const updateProgress = (progress: number, text: string) => {
      api.open({
        key: notificationKey,
        message: "下载进度",
        description: (
          <div>
            <div style={{ marginBottom: "8px", fontSize: "14px" }}>{text}</div>
            <Progress
              percent={progress}
              size="small"
              status={progress === 100 ? "success" : "active"}
              strokeColor="#1890ff"
              showInfo={false}
            />
          </div>
        ),
        duration: 0,
        placement: "topRight",
        style: {
          width: 320,
          minHeight: 80,
        },
        className: "download-progress-notification",
      });
    };

    try {
      updateProgress(0, "正在获取视频列表...");

      // 按剧集 + 语种获取视频列表
      const videoListResponse = await getTaskListApi({
        seriesId: item.seriesId,
        langCode: item.langCode,
      });

      const completedVideos = videoListResponse.data.filter(
        (video: any) => video.status === 2 && getVideoPlayUrl(video)
      );

      if (completedVideos.length === 0) {
        api.destroy(notificationKey);
        message.error("该语种下没有可下载的已完成视频");
        return;
      }

      updateProgress(5, `找到 ${completedVideos.length} 个可下载视频，准备开始下载...`);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const zip = new JSZip();
      const downloadPromises: Promise<void>[] = [];
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < completedVideos.length; i++) {
        const video: any = completedVideos[i];
        const videoUrl = getVideoPlayUrl(video);

        if (videoUrl) {
          const downloadPromise = fetch(videoUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
              return response.blob();
            })
            .then((blob) => {
              const safeFileName = (video.name || "video").replace(/[<>:"/\\|?*]/g, "_");
              const fileName = `${safeFileName}.mp4`;
              zip.file(fileName, blob);
              successCount++;

              const downloadProgress =
                5 + Math.round(((i + 1) / completedVideos.length) * 85);
              updateProgress(
                downloadProgress,
                `正在下载: ${video.name} (${i + 1}/${completedVideos.length})`
              );
            })
            .catch((error) => {
              console.error(`下载视频 ${video.name} 失败:`, error);
              failCount++;

              const downloadProgress =
                5 + Math.round(((i + 1) / completedVideos.length) * 85);
              updateProgress(
                downloadProgress,
                `下载失败: ${video.name} (${i + 1}/${completedVideos.length})`
              );
            });

          downloadPromises.push(downloadPromise);
        }
      }

      await Promise.allSettled(downloadPromises);

      if (successCount === 0) {
        api.destroy(notificationKey);
        message.error("所有视频下载失败，请检查网络连接");
        return;
      }

      updateProgress(90, "正在生成压缩包...");

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6,
        },
      });

      updateProgress(100, "压缩包生成完成，正在下载...");
      await new Promise((resolve) => setTimeout(resolve, 500));

      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);

      const safeSeriesName = (item.name || "series").replace(/[<>:"/\\|?*]/g, "_");
      const languageName = item.langCode ? getLanguageName(item.langCode) : "未知语种";
      const safeLanguageName = languageName.replace(/[<>:"/\\|?*]/g, "_");
      link.download = `${safeSeriesName}_${safeLanguageName}_全集.zip`;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);

      api.destroy(notificationKey);

      if (failCount > 0) {
        message.info(
          `下载完成！成功下载 ${successCount} 个视频，${failCount} 个视频下载失败`
        );
      } else {
        message.success(
          `剧集 "${item.name}"（${item.langCode}）下载完成，共 ${successCount} 个视频`
        );
      }
    } catch (error) {
      api.destroy(notificationKey);
      message.error("批量下载失败，请稍后重试");
      console.error("批量下载错误:", error);
    }
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(1); // 切换排序时重置到第一页
  };
  
  // 搜索输入处理函数（带防抖优化）
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 立即更新输入框显示的值，保证UI响应流畅
    setSearchInputValue(value);
    
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // 设置新的防抖定时器，延迟更新 searchQuery 触发API调用
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1); // 搜索时重置到第一页
    }, 1000); // 1秒防抖延迟
  };
  
  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  
  // 同步 searchQuery 到 searchInputValue（防御性处理，确保状态一致）
  useEffect(() => {
    // 只有在 searchQuery 不是由用户输入触发时才同步
    // 例如：重置搜索时
    if (searchQuery === '' && searchInputValue !== '') {
      setSearchInputValue('');
    }
  }, [searchQuery]);

  // 点击卡片：跳转到视频列表页
  // seriesId 来自首页点击的剧集 ID，langCode 为当前语言卡片的语言编码
  const handleCardClick = (seriesId: number, seriesName: string, langCode?: string) => {
    navigate(
      `/videoList?seriesId=${seriesId}&seriesName=${encodeURIComponent(
        seriesName || ""
      )}${langCode ? `&langCode=${encodeURIComponent(langCode)}` : ""}`
    );
  };

  // 查看纯净版视频列表详情 - 权限校验 + 自动补救
  const handleViewPureVideoList = async () => {
    // 1. 先用当前缓存做一次"快速放行"判断（只在有权限时直接通过）
    let roleInfo = UserRoleManager.getCurrentUserRole();
    if (roleInfo && (roleInfo.isAdmin || roleInfo.isAssessor || roleInfo.isUser)) {
      proceedToVideoList();
      return;
    }

    // 2. 走到这里说明：要么没有角色信息，要么根据当前缓存算出来是无权限
    //    按你的思路，这里不立刻判死刑，而是先尝试重新同步一次用户/菜单数据
    try {
      console.log('【权限检查】缓存验证未通过，尝试强制同步用户信息...');
      await InitService.initializeUserData();

      // 3. 同步完成后，基于最新数据再计算一次角色信息
      roleInfo = UserRoleManager.getCurrentUserRole();
      if (roleInfo && (roleInfo.isAdmin || roleInfo.isAssessor || roleInfo.isUser)) {
        console.log('【权限检查】同步后权限验证通过');
        proceedToVideoList();
        return;
      }

      // 4. 二次校验依然无权限，才算“真无权限”
      console.warn('【权限检查】同步后仍无权限，拒绝访问');
      setAlertConfig({
        type: 'warning',
        message: '您暂无权限访问',
      });
    } catch (error) {
      console.error('【权限检查】用户信息同步失败', error);
      setAlertConfig({
        type: 'error',
        message: '用户信息验证失败，请尝试刷新页面',
      });
    }

    // 跳转逻辑提取为独立函数
    function proceedToVideoList() {
      if (!seriesId) {
        message.warning('剧集ID不存在');
        return;
      }

      // 直接跳转到视频列表页，传递 isPure 标识，在视频列表页调用接口
      // 使用 displaySeriesName（外层剧集名）进行跳转
      const seriesNameToPass = displaySeriesName || '';
      const targetUrl = `/videoList?seriesId=${seriesId}&seriesName=${encodeURIComponent(
        seriesNameToPass
      )}&isPure=true`;

      navigate(targetUrl);
    }
  };

  // 选择角色：直接使用左侧列表中该角色携带的 subtitles（仅使用真实数据）
  const handleSpeakerSelect = async (speakerId: number, speakerName: string) => {
    setSelectedSpeakerId(speakerId);
    setSelectedSpeakerName(speakerName);
    try {
      setLoadingSubtitles(true);
      const current = seriesSpeakers.find(s => s.id === speakerId) as any;
      const subs = current && current.subtitles ? current.subtitles : [];
      const sortedSubtitles = subs.sort ? subs.sort((a: any, b: any) => (b.score || 0) - (a.score || 0)) : subs;
      setSpeakerSubtitles(sortedSubtitles);
    } catch (error) {
      setSpeakerSubtitles([]);
    } finally {
      setLoadingSubtitles(false);
    }
  };

  // 播放音色片段
  const handlePlayClip = (subtitle: any) => {
    const audioUrl = subtitle.origin_audio || subtitle.trans_audio;
    if (!audioUrl) {
      setAlertConfig({ type: 'warning', message: '该片段暂无音频' });
      return;
    }

    // 停止当前播放的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // 如果点击的是同一个音频，则停止播放
    if (playingClipId === subtitle.id) {
      setPlayingClipId(null);
      return;
    }

    try {
      const fullUrl = audioUrl.startsWith('http') ? audioUrl : `${CDN_PREFIX}${audioUrl}`;
      console.log('播放音频链接:', fullUrl);
      const audio = new Audio(fullUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingClipId(null);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setAlertConfig({ type: 'error', message: '音频播放失败' });
        setPlayingClipId(null);
        audioRef.current = null;
      };

      audio.play();
      setPlayingClipId(subtitle.id);
    } catch (error) {
      console.error('播放音频失败:', error);
      setAlertConfig({ type: 'error', message: '播放音频失败' });
      setPlayingClipId(null);
    }
  };

  // 移除音色片段
  const handleRemoveClip = async (subtitleId: number) => {
    if (!selectedSpeakerId) {
      setAlertConfig({ type: 'warning', message: '请先选择角色' });
      return;
    }
    try {
      const res = await demoteSpeakerSubtitleApi(subtitleId);
      if (res && res.code === 0) {
        // 更新右侧字幕列表
        setSpeakerSubtitles(prev => prev.filter((s: any) => s.id !== subtitleId));
        // 同步更新左侧该说话人的 subtitles
        setSeriesSpeakers(prev => prev.map(s => {
          if (s.id !== selectedSpeakerId) return s;
          const current: any = s as any;
          const subs = Array.isArray(current.subtitles) ? current.subtitles.filter((x: any) => x.id !== subtitleId) : [];
          return { ...s, subtitles: subs } as any;
        }));
        setAlertConfig({ type: 'success', message: res.msg || '已移除该音色片段' });
      } else {
        setAlertConfig({ type: 'error', message: res?.msg || '移除失败' });
      }
    } catch (e: any) {
      setAlertConfig({ type: 'error', message: e?.message || '移除失败' });
    }
  };

  // 删除角色
  const handleDeleteSpeaker = async (speakerId: number) => {
    try {
      const res: any = await deleteSpeakerApi(speakerId);
      setSeriesSpeakers(prev => prev.filter(s => s.id !== speakerId));
      // 如果当前选中被删，清空右侧
      setSpeakerSubtitles(prev => (selectedSpeakerId === speakerId ? [] : prev));
      if (selectedSpeakerId === speakerId) {
        setSelectedSpeakerId(null);
        setSelectedSpeakerName('');
      }
      setAlertConfig({ type: 'success', message: res?.message || '已删除该角色' });
    } catch (e: any) {
      setAlertConfig({ type: 'error', message: e?.message || '删除角色失败' });
    }
  };

  // 删除剧集语种
  const handleDeleteSeriesLanguage = async (languageId: number, languageName: string) => {
    try {
      const res = await deleteSeriesLanguageApi(languageId);
      if (res.code === 0 && res.data) {
        setAlertConfig({ type: 'success', message: `剧集语种 "${languageName}" 删除成功` });
        // 重新同步数据
        const fetchData = async () => {
          if (!seriesId) return;
          try {
            setLoading(true);
            const response = await getSeriesLanguagesPageApi({
              pageNo: page,
              pageSize,
              seriesId,
            });
            const list = response?.data?.list || [];
            const total = response?.data?.total ?? 0;
            setApiData((prev) => ({
              data: list,
              pagination: {
                ...prev.pagination,
                page,
                per_page: pageSize,
                total,
                pages: Math.max(1, Math.ceil(total / pageSize || 1)),
                has_next: page * pageSize < total,
                has_prev: page > 1,
              },
            }));
          } catch (err) {
            setAlertConfig({ type: 'error', message: '重新加载数据失败' });
          } finally {
            setLoading(false);
          }
        };
        fetchData();
      } else {
        setAlertConfig({ type: 'error', message: res.msg || '删除剧集语种失败' });
      }
    } catch (error: any) {
      setAlertConfig({ type: 'error', message: error?.message || '删除剧集语种失败，请稍后重试' });
    }
  };

  // 开始创建角色
  const handleStartCreateRole = () => {
    setIsCreatingRole(true);
    setNewRoleName('');
  };

  // 确认创建角色
  const handleConfirmCreateRole = async () => {
    if (!newRoleName.trim()) {
      // 如果输入为空，直接恢复到原始状态
      setIsCreatingRole(false);
      setNewRoleName('');
      return;
    }
    try {
      // 获取 langCode：优先使用 selectedLangCode，其次使用第一个语言项的 langCode，最后使用 targetLanguage
      const langCodeToUse = selectedLangCode || 
                           (apiData.data && apiData.data.length > 0 && apiData.data[0].langCode) || 
                           currentEpisodeInfo.targetLanguage || 
                           'en';
      
      if (!selectedSeriesId) {
        setAlertConfig({ type: 'error', message: '缺少剧集ID' });
        return;
      }
      
      const params: Speakers.CreateSpeakerParams = {
        seriesId: selectedSeriesId,
        speakerName: newRoleName.trim(),
        langCode: langCodeToUse
      };
      const res = await createSpeakerApi(params);
      const created = (res as any)?.data as Speakers.Speaker | undefined;
      if (created && created.id) {
        setSeriesSpeakers(prev => [...prev, created]);
        setAlertConfig({ type: 'success', message: (res as any)?.message || `新建说话人成功：${created.speaker_name}` });
      } else {
        setAlertConfig({ type: 'error', message: '创建角色失败，请重试' });
      }
    } catch (e: any) {
      setAlertConfig({ type: 'error', message: e?.message || '创建角色失败' });
    } finally {
      setIsCreatingRole(false);
      setNewRoleName('');
    }
  };

  // 取消创建角色（点击勾选按钮恢复状态）
  const handleCancelCreateRole = () => {
    setIsCreatingRole(false);
    setNewRoleName('');
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    // 停止播放的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingClipId(null);
    setShowVoiceModal(false);
    setSelectedSeriesId(null);
    setSelectedSeriesName('');
    setSelectedLangCode(''); // 重置语言代码
    setSelectedSpeakerId(null);
    setSeriesSpeakers([]);
    setSpeakerSubtitles([]);
    // 重置新建角色状态
    setIsCreatingRole(false);
    setNewRoleName('');
  };

  // 计算显示的剧集名，优先级：currentEpisodeInfo.name > URL参数 > API数据中的第一个item
  const displaySeriesName = useMemo(() => {
    if (currentEpisodeInfo.name) {
      return currentEpisodeInfo.name;
    }
    // 从URL参数中获取
    const params = new URLSearchParams(location.search);
    const nameFromUrl = params.get("seriesName");
    if (nameFromUrl) {
      return decodeURIComponent(nameFromUrl);
    }
    // 从API数据中获取第一个item的seriesName
    if (apiData.data && apiData.data.length > 0) {
      const firstItem = apiData.data[0];
      return firstItem.seriesName ?? firstItem.name ?? firstItem.series_name ?? '';
    }
    return '';
  }, [currentEpisodeInfo.name, location.search, apiData.data]);

  return (
    <div className="sseriesListIn-page">
      <AlertMessage config={alertConfig} onClose={() => setAlertConfig(null)} zIndex={ALERT_Z_INDEX.UPLOAD_VIDEO} />
      <div className="public_voices" onClick={handleViewPureVideoList} style={{ cursor: 'pointer' }}>
        <div className="public_left">
          <div>
            <img src="/img/chun.png" alt="纯净版图标" className="voice-icon" />
            <div className="voice-title">{displaySeriesName ? `${displaySeriesName}—原剧管理` : '—原剧管理'}</div>
          </div>
          <div className="voice-description">Tips：在生成多语种翻译配音前，您可以在此处进行字幕擦除、剧本解析、台词校对哦~</div>
        </div>
        <div className="public">
          <img src="/img/chun1.png" alt="纯净版图标" className="public_center" />
          <Button className="public_right" onClick={(e) => {
            e.stopPropagation(); // 阻止事件冒泡，避免触发两次
            handleViewPureVideoList();
          }}>点击上传视频 &gt;</Button>
        </div>
      </div>

      {/* 搜索和排序区域 */}
      <div className="voice-search-filter-bar">
        <div className="page-title">多语种版本</div>
        <div className="search-sort-container">
          <div className="search-section">
            <div className="search-bar">
              <i className="fas fa-search search-icon"></i>
              <Input
                placeholder="搜索语种"
                className="search-input show"
                value={searchInputValue}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="sort-section">
            <Select
              value={sortBy}
              onChange={handleSortChange}
              className="sort-dropdown"
              placeholder="最新创建"
            >
              <Select.Option value="latest">最新创建</Select.Option>
              <Select.Option value="earliest">最早创建</Select.Option>
              <Select.Option value="name-asc">按名称</Select.Option>
            </Select>
          </div>
          <EpisodeDrawer
            episodeInfo={currentEpisodeInfo}
            speakersData={drawerSpeakers}
            speakersLoading={drawerSpeakersLoading}
            seriesName={currentEpisodeInfo.name}
            characterProfiles={characterProfiles}
          />
        </div>
      </div>

      {/* 卡片网格 */}
      {contextHolder}
      {loading ? (
        <div className="voices-grid">
          {/* 加载中状态可以显示骨架屏 */}
        </div>
      ) : pagedData.length === 0 ? (
        <div className="empty-search-container">
          <div className="empty-search-content">
            <div className="empty-icon">
              <i className="fas fa-folder"></i>
            </div>
            <div className="empty-text">
              暂无多语种版本
            </div>
          </div>
        </div>
      ) : (
        <div className="voices-grid">
          {pagedData.map((item) => (
          <div
            key={item.id}
            className="voices-card"
            onClick={() => handleCardClick(item.seriesId, item.name, item.langCode)}
          >
            {item.isNew && <div className="new-badge">⚡</div>}
            <div className="card-content">
              <div className="card-left">
                <img src="/img/film.png" alt="音色图标" className="card-icon" />
              </div>
              <div className="card-right">
                <div className="card-title-wrapper">
                  <div 
                    className="card-title"
                    title={
                      item.langCode 
                        ? `${item.name} — ${getLanguageName(item.langCode)}`
                        : item.name
                    }
                  >
                    {item.name}
                    {item.langCode && (
                      <span className="card-lang-label">
                        {" — "}
                        {getLanguageName(item.langCode)}
                      </span>
                    )}
                  </div>
                  {/* 根据 tagStatus 显示状态：1=审核通过，2=需返工，3=待审核 */}
                  {item.tagStatus === 1 && (
                    <div className="card-status-badge">审核通过</div>
                  )}
                  {item.tagStatus === 2 && (
                    <div className="card-status-badge rework">需返工</div>
                  )}
                  {item.tagStatus === 3 && (
                    <div className="card-status-badge pending">待审核</div>
                  )}
                </div>
                <div className="card-subtitle">
                  {item.createTime || "--"}
                </div>
              </div>
              <div className="card-actions">
                {hasSeriesDownloadPermission() && (
                  <img 
                    src="/img/download.png" 
                    alt="下载" 
                    className="card-action-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLanguageDownload(item);
                    }}
                  />
                )}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Popconfirm
                    title="确定要删除这个剧集语种吗？"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      const languageName = item.langCode 
                        ? `${item.name} — ${getLanguageName(item.langCode)}`
                        : item.name;
                      handleDeleteSeriesLanguage(item.id, languageName);
                    }}
                    okText="确定"
                    cancelText="取消"
                    onCancel={(e) => {
                      e?.stopPropagation();
                    }}
                  >
                    <img 
                      src="/img/delete.png" 
                      alt="删除" 
                      className="card-action-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    />
                  </Popconfirm>
                </span>
                <img src="/img/jiantou.png" alt="箭头" className="card-arrow-icon" />
              </div>
            </div>
          </div>
          ))}
        </div>
      )}

      {pagedData.length > 0 && (
        <MiniPaginator
          page={apiData.pagination.page}
          pages={pages}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onChange={setPage}
          className="mini-pagination-voice"
        />
      )}

      {/* 剧集音色库弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src="/img/cover-blue.png" alt="音色图标" style={{ width: '40px', height: '40px' }} />
            <span style={{ fontSize: '22px' }}>{selectedSeriesName}</span>
          </div>
        }
        open={showVoiceModal}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        className="voice-library-modal"
        style={{ top: '15%' }}
      >
        <div style={{ display: 'flex', gap: '20px', minHeight: '400px' }}>
          {/* 左侧：角色列表 */}
          <div style={{ width: '300px', borderRight: '1px solid #e0e0e0', paddingRight: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#333' }}>剧集音色库</h3>

            <div style={{
              display: 'flex',
              marginBottom: '16px',
              transition: 'all 0.3s ease',
              alignItems: 'center',
              width: '100%'
            }}>
              <Input
                placeholder="请输入角色名"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onPressEnter={handleConfirmCreateRole}
                autoFocus={isCreatingRole}
                style={{
                  flex: isCreatingRole ? 1 : 0,
                  width: isCreatingRole ? 'auto' : 0,
                  opacity: isCreatingRole ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  margin: 0,
                  marginRight: isCreatingRole ? '5px' : 0,
                  padding: isCreatingRole ? undefined : 0,
                  border: isCreatingRole ? undefined : 'none',
                  visibility: isCreatingRole ? 'visible' : 'hidden',
                  height: '40px',
                  borderRadius: 0
                }}
                className="new-role-input"
              />
              <Button
                type="primary"
                icon={isCreatingRole ? <i className="fas fa-check"></i> : <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>}
                onClick={isCreatingRole ? handleConfirmCreateRole : handleStartCreateRole}
                style={{
                  height: '40px',
                  minWidth: isCreatingRole ? '40px' : 'auto',
                  width: isCreatingRole ? '40px' : '100%',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  marginLeft: 'auto',
                  borderRadius: '5px'
                }}
                className={isCreatingRole ? "confirm-create-role-btn" : "create-role-btn"}
              >
                {!isCreatingRole && '新建角色'}
              </Button>
            </div>

            {loadingSpeakers ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>加载中...</div>
            ) : seriesSpeakers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>暂无角色</div>
            ) : (
              <div style={{ height: '360px', overflowY: 'auto', marginBottom: '10px' }} className="voice-library-scroll">
                {seriesSpeakers.map((speaker) => (
                  <div
                    key={speaker.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      border: selectedSpeakerId === speaker.id ? '1px solid #4a90e2' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: selectedSpeakerId === speaker.id ? '#f0f8ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => handleSpeakerSelect(speaker.id, speaker.speaker_name)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <i className="fas fa-music" style={{ color: '#4a90e2', fontSize: '16px' }}></i>
                      <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>{speaker.speaker_name}</span>
                    </div>
                    <img
                      src="/img/del.png"
                      alt="删除"
                      style={{ width: '27px', height: '27px', cursor: 'pointer', padding: '4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSpeaker(speaker.id);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 右侧：音色片段列表 */}
          <div style={{ flex: 1, paddingLeft: '20px' }}>
            {selectedSpeakerId ? (
              <>
                <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                  {selectedSpeakerName}的音色
                </div>

                {loadingSubtitles ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>加载中...</div>
                ) : speakerSubtitles.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>暂无音色片段</div>
                ) : (
                  <div style={{ height: '420px', overflowY: 'auto', marginBottom: '10px' }} className="voice-library-scroll">
                    {speakerSubtitles.map((subtitle: any) => (
                      <div
                        key={subtitle.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          marginBottom: '12px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                        }}
                      >
                        <span style={{ fontSize: '14px', color: '#333', flex: 1 }}>{subtitle.origin_text || subtitle.text}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button
                            onClick={() => handlePlayClip(subtitle)}
                            style={{
                              width: '24px',
                              height: '24px',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {playingClipId === subtitle.id ? (
                              <i className="fas fa-pause" style={{ color: '#000', fontSize: '14px' }}></i>
                            ) : (
                              <i className="fas fa-play" style={{ color: '#000', fontSize: '14px' }}></i>
                            )}
                          </button>
                          <Button
                            size="small"
                            danger
                            onClick={() => handleRemoveClip(subtitle.id)}
                          >
                            移除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                请选择左侧角色查看音色片段
              </div>
            )}
          </div>
        </div>
      </Modal>

      <CopyrightFooter />
    </div>
  )
};
